import { FunctionPermissionDto } from "../types/shared-dbtypes";
import { Lazy } from "../core/lazy";
import { UrlHelper } from "../core/html/url-helper";
import { Logger } from "../logger/logger";
import { NotPermissionError } from "../core/exception/not-permission-error";
import { SessionStorageManager } from "../core/storage/session-storage-manager";
import { ServiceProvider } from "../core/provider/service-provider";
import { DialogResultVm } from "../checkresultdialog/dialogresultvm";
import { CheckResultData } from "../checkresultdialog/checkresultdata";
import { AjaxParams } from "../core/provider/provider-interfaces";

export class UserFunctionPermissionService {
	public static getInstance: () => UserFunctionPermissionService = Lazy.singleton(UserFunctionPermissionService);
	constructor() {
		/**/
	}

	public cachePermissions: KnockoutObservableArray<FunctionPermissionDto> = ko.observableArray([]);

	/**
	 *	Wenn in CustomerSettings.config key CheckUserFunctionPermissions true oder null ist, dann werden alle Permissions von Server gelesen.
	 *	Wenn es false ist, dann die Eigenschaft granted bei bei allen Permissions auf true gesetzt.
	 **/
	public loadFunctionPermissionsWithCacheAsAsync = async (permissionIds: number[]): Promise<any> => {
		if (!permissionIds.length) {
			return;
		}

		let result: any | FunctionPermissionDto[] = null;

		let checkingKeyGlobalStatus: boolean = this.checkGlobalStatus();

		if (!checkingKeyGlobalStatus && SessionStorageManager.getInstance().hasKey("checkingKeyGlobalStatus")) {
			let res = await this.postJsonFromPermissionsAsAsync(permissionIds);
			this.checkUserFunctionPermission(res, checkingKeyGlobalStatus);
		}

		if (checkingKeyGlobalStatus && !SessionStorageManager.getInstance().hasKey("checkingKeyGlobalStatus")) {
			SessionStorageManager.getInstance().removeKey("checkingKeyGlobalStatus");
			SessionStorageManager.getInstance().removeKey("permissions");
		}

		let permissionsFromStorage = this.getPermissionStorage("permissions");

		if (typeof permissionsFromStorage === "boolean" && !permissionsFromStorage) {
			result = await this.postJsonFromPermissionsAsAsync(permissionIds);

			if (Array.isArray(result) && result.length) {
				this.setPermissionsStorage("permissions", JSON.stringify(result));
			}
		}

		if (typeof permissionsFromStorage !== "boolean") {
			let promissinsAsArray: FunctionPermissionDto[] = permissionsFromStorage;

			if (!promissinsAsArray.length) {
				throw new NotPermissionError("Permissions sind im SessionStorage nicht vorhanden.");
			}

			for (let item of promissinsAsArray) {
				let index = permissionIds.indexOf(item.id);
				if (index > -1) {
					permissionIds.splice(index, 1);
				}
			}

			if (!permissionIds.length) {
				result = this.getPermissionStorage("permissions");
			} else {
				let tmpResul: FunctionPermissionDto[] = await this.postJsonFromPermissionsAsAsync(permissionIds);
				result = promissinsAsArray.concat(tmpResul).sort((a, b) => {
					return a.id - b.id;
				});

				this.setPermissionsStorage("permissions", JSON.stringify(result));
			}
		}
		if (result) {
			this.cachePermissions(result);
		}

		Logger.log(this.cachePermissions());
	}

	public hasFunctionPermissionComputed = (permissionId: number) => ko.computed(() => {
		let result: boolean = false;
		let curCachePermission = this.cachePermissions();

		if (!curCachePermission || curCachePermission.length === 0) {
			Logger.warn("No permissions loaded", permissionId);
			return result;
		}

		let foundPermission = curCachePermission.find((perm) => perm.id === permissionId);
		if (!foundPermission) {
			Logger.warn(`No permission loaded with id ${permissionId}`);
			return result;
		}

		return foundPermission.granted;
	})

	public hasFunctionPermission = (permissionId: number) => {
		return this.hasFunctionPermissionComputed(permissionId)();
	}

	public get(permissionId: number): FunctionPermissionDto | NotPermissionError {
		let curCachePermissions = this.cachePermissions();
		if (!curCachePermissions || curCachePermissions.length === 0) {
			Logger.warn("No permission loaded", permissionId);
			throw new NotPermissionError(`Die PermissioId: ${permissionId} ist nicht vorhanden.`);
		}
		let permission = curCachePermissions.filter((item) => item.id === permissionId);
		let permissionDto = new FunctionPermissionDto();

		if (permission.length !== 1) {
			throw new NotPermissionError(`Die PermissioId: ${permissionId} ist nicht vorhanden.`);
		}

		for (let item of curCachePermissions) {
			if (item.id === permissionId && item.granted === true) {
				permissionDto.id = item.id;
				permissionDto.name = item.name;
				permissionDto.description = item.description;
				permissionDto.granted = item.granted;
				break;
			} else {
				throw new NotPermissionError(`Sie haben keine Berechtigung für die Function: ${item.name}`);
			}
		}

		return permissionDto;
	}

	/**
	 * key in SessonStorage = 'permissions'
	 */
	public getPermissionStorage(key: string = "permissions"): FunctionPermissionDto[] | boolean {
		if (SessionStorageManager.getInstance().hasKey(key)) {
			return false;
		}

		let value = SessionStorageManager.getInstance().getKey(key);
		if (!value) {
			Logger.log(`Key: ${key} in SessionStorage ist leer. Value gleich null.`);
			return false;
		}

		let valueArray: FunctionPermissionDto[] = JSON.parse(value);
		if (!Array.isArray(valueArray) || !valueArray.length) {
			Logger.log(`Array FunctionPermissionDto[] ist leer.`);
			return false;
		}

		return valueArray;
	}

	private postJsonFromPermissionsAsAsync = async (permissionIds: number[]): Promise<FunctionPermissionDto[]> => {
		let params = { permissionIds };
		return await this.postJson(params);
	}

	private postJson = async (params: AjaxParams): Promise<FunctionPermissionDto[]> => {
		let result: CheckResultData = await ServiceProvider.ajax
			.postJsonAsync({ url: UrlHelper.getDataUrl("user-function-permissionIds"), params })
			.catch((err) => { Logger.warn(`could not load permissionIds`, err); return; });

		if (!result || !result.success) {
			await DialogResultVm.showDialogAndGetResultAsync(result);
			return null;
		} else {
			return result.resultData;
		}
	}

	private checkGlobalStatus = (): boolean => {
		let globalStatus = $("body").data("check-global-status-user-function-permissions");

		let check: boolean = (globalStatus === "True") ? true : globalStatus === true ? true : false; //TODO...
		return check;
	}

	private checkUserFunctionPermission: (permissions: any[], checkingKeyGlobalStatus: boolean) => boolean = (permissions, checkingKeyGlobalStatus) => {
		if (!checkingKeyGlobalStatus) {
			if (SessionStorageManager.getInstance().hasKey("checkingKeyGlobalStatus")) { // true is null
				if (Array.isArray(permissions)) {
					this.checkKeyAndFunctionsFromPermissionSave(!checkingKeyGlobalStatus, permissions);
				}
			} else {
				if (this.getPermissionCheckKey("checkingKeyGlobalStatus") === !checkingKeyGlobalStatus) {
					return false;
				}

				if (this.getPermissionCheckKey("checkingKeyGlobalStatus") === checkingKeyGlobalStatus) {
					if (!SessionStorageManager.getInstance().hasKey("checkingKeyGlobalStatus")) {
						SessionStorageManager.getInstance().removeKey("checkingKeyGlobalStatus");
						SessionStorageManager.getInstance().removeKey("permissions");
					}
				}
			}
		}

		return true;
	}

	private setPermissionsStorage(key: string, value: any): boolean {
		if (!value.length) {
			return false;
		}
		return SessionStorageManager.getInstance().setKey(key, value);
	}

	private checkKeyAndFunctionsFromPermissionSave: (check: boolean, functions: any[]) => void = (check, functions) => {
		if (functions.length) {
			for (let item of functions) {
				item.granted = check;
			}
			/**
			 *  PermissionCheckKey wird im SessionStorage gespeichert
			 * */
			this.setPermissionCheckKey("checkingKeyGlobalStatus", JSON.stringify(check));
			this.setPermissionsStorage("permissions", JSON.stringify(functions));
		}
	}

	private getPermissionCheckKey: (key: string) => boolean = (key) => {
		return JSON.parse(SessionStorageManager.getInstance().getKey(key));
	}

	private setPermissionCheckKey: (key: string, value: string) => boolean = (key, value) => {
		return SessionStorageManager.getInstance().setKey(key, value);
	}
}